import React, { useState, useEffect } from 'react';
import {
  Box,
  Modal,
  IconButton,
  Paper,
  TextField as MuiTextField,
  Typography
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { TextView, Button, Alert } from '@atoms';
import { BillingAccountSelector } from '@organisms';

import { callUpdateKey, useBillingAccounts } from '@particles';

interface EditKeyModalProps {
  open: boolean;
  onClose: () => void;
  apiKey: Keys | null;
  locationIdToName: Record<string, string>;
}

const EditKeyModal: React.FC<EditKeyModalProps> = ({ open, onClose, apiKey, locationIdToName }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedLocations, setSelectedLocations] = useState<BillingAccount[]>([]);
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [error, setError] = useState<{ title: string; message: string } | undefined>();

  const { data: billingResponse, isFetching: loadingLocations } = useBillingAccounts({
    type: 'c',
    status: 'live',
    caller: 'edit-api-key'
  });
  const locationOptions: BillingAccount[] = billingResponse?.data?.data ?? [];

  useEffect(() => {
    if (apiKey && open) {
      // Pre-select existing locations
      const existingIds = apiKey.locationIds ?? [];
      const preSelected = locationOptions.filter(
        (opt) => opt.locationId && existingIds.includes(opt.locationId)
      );
      setSelectedLocations(preSelected);

      // Pre-fill expiry date
      if (apiKey.expiresAt) {
        const date = new Date(apiKey.expiresAt);
        setExpiresAt(date.toISOString().split('T')[0]);
      } else {
        setExpiresAt('');
      }

      setError(undefined);
    }
  }, [apiKey, open, locationOptions.length]);

  const handleClose = () => {
    setError(undefined);
    onClose();
  };

  const handleUpdate = async () => {
    if (!apiKey) return;

    if (selectedLocations.length === 0) {
      setError({ title: 'Validation', message: 'Please select at least one location' });
      return;
    }

    setLoading(true);
    setError(undefined);

    try {
      const res = await callUpdateKey(apiKey.id, {
        locationIds: selectedLocations
          .map((l) => l.locationId)
          .filter((id): id is string => !!id),
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null
      });

      if (res.code === 200) {
        handleClose();
      } else {
        setError({
          title: 'Error',
          message: (res as any).error?.message ?? 'Failed to update key'
        });
      }
    } catch (err: any) {
      setError({ title: 'Error', message: err?.message ?? String(err) });
    } finally {
      setLoading(false);
    }
  };

  const isClientKey = apiKey && Array.isArray(apiKey.locationIds) && apiKey.locationIds.length > 0;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Paper sx={{ p: 3, width: '100%', maxWidth: 480, mx: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <TextView $fontSize={22}>Edit API Key Scope</TextView>
          <IconButton size="small" onClick={handleClose} aria-label="Close">
            <Close />
          </IconButton>
        </Box>

        {!isClientKey ? (
          <>
            <Alert
              severity="info"
              title="Not editable"
              message="Legacy/webhook keys do not have location scope. Only client keys can be edited."
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button label="Close" size="small" onClick={handleClose} />
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Key: <strong>{apiKey?.name}</strong>
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Update the locations and expiry for this client API key.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
              <BillingAccountSelector
                title="Locations"
                type="c"
                status="live"
                caller="edit-api-key"
                multiple
                required
                width="100%"
                placeholder={selectedLocations.length === 0 ? 'Select one or more locations' : ''}
                value={selectedLocations}
                onSelected={setSelectedLocations}
              />
              <MuiTextField
                size="small"
                label="Expires (optional)"
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>

            {error && <Alert severity="error" title={error.title} message={error.message} />}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
              <Button label="Cancel" size="small" onClick={handleClose} />
              <Button label="Update" size="small" onClick={handleUpdate} loading={loading} />
            </Box>
          </>
        )}
      </Paper>
    </Modal>
  );
};

export default EditKeyModal;
